import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  HStack,
  Input,
  SimpleGrid,
  Stack,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import CustomerList from '../components/CustomerList';
import CustomerModals from '../components/CustomerModals';
import ConfirmationModal from '../components/ConfirmationModal';
import usePagination from '../utils/usePagination';
import api from '../api';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    edrpou_code: '',
    address: '',
    bank_name: '',
    bank_number: '',
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  const fetchCustomers = async (url = `/customers/?search=${search}`) => {
    try {
      const response = await api.get(url);
      setCustomers(response.data.results);
      updatePagination(response.data); // Use pagination hook to update state
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const { pagination, updatePagination, fetchNext, fetchPrevious, goToPage } = usePagination(fetchCustomers, '/customers/');

  useEffect(() => {
    fetchCustomers();
  }, [search]);

  const handleCreate = async () => {
    try {
      await api.post('/customers/', formData);
      fetchCustomers();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Failed to create customer:', error);
    }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/customers/${formData.id}/`, formData);
      fetchCustomers();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to edit customer:', error);
    }
  };

  const handleOpenDeleteConfirm = (customer) => {
    setCustomerToDelete(customer);
    setIsConfirmationModalOpen(true);
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      await api.delete(`/customers/${customerToDelete.id}/`);
      fetchCustomers();
    } catch (error) {
      console.error('Failed to delete customer:', error);
    } finally {
      setIsConfirmationModalOpen(false);
      setCustomerToDelete(null);
    }
  };

  return (
    <Box p="6">
      <Flex justify="space-between" align="center" mb="6">
        <Text fontSize="2xl" fontWeight="bold">
          Клієнти
        </Text>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={() => setIsCreateModalOpen(true)}>
          Додати клієнта
        </Button>
      </Flex>

      {/* Search Filter */}
      <Input
        placeholder="Пошук"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        mb="6"
      />

      {/* Customer Tiles */}
      <SimpleGrid columns={[1, 2, 3]} spacing={6}>
        <CustomerList
          customers={customers}
          onEdit={(customer) => {
            setFormData(customer);
            setIsEditModalOpen(true);
          }}
          onDelete={handleOpenDeleteConfirm} // Pass confirmation handler
        />
      </SimpleGrid>

      {/* Pagination */}
      {pagination.count > 0 && (
        <Flex justify="center" mt="6" align="center">
          {pagination.count > 12 && ( // Only show pagination if there are more than 10 items
            <HStack spacing="2">
              {/* Previous Button */}
              <Button
                size="sm"
                onClick={() => {
                  fetchPrevious();
                }}
                isDisabled={!pagination.previous}
              >
                Назад
              </Button>
        
              {/* Dynamic Page Numbers */}
              {Array.from({ length: Math.ceil(pagination.count / 12) }, (_, index) => (
                <Button
                  key={index + 1}
                  size="sm"
                  colorScheme={pagination.currentPage === index + 1 ? 'blue' : 'gray'}
                  onClick={() => {
                    goToPage(index + 1); // Move to selected page
                  }}
                >
                  {index + 1}
                </Button>
              ))}
        
              {/* Next Button */}
              <Button
                size="sm"
                onClick={() => {
                  fetchNext();
                }}
                isDisabled={!pagination.next}
              >
                Вперед
              </Button>
            </HStack>
          )}
        </Flex>         
      )}

      {/* Modals */}
      <CustomerModals
        isCreateModalOpen={isCreateModalOpen}
        isEditModalOpen={isEditModalOpen}
        onCloseCreate={() => setIsCreateModalOpen(false)}
        onCloseEdit={() => setIsEditModalOpen(false)}
        onSubmitCreate={handleCreate}
        onSubmitEdit={handleEdit}
        formData={formData}
        setFormData={setFormData}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleDeleteCustomer}
        message="Ви впевнені, що хочете видалити цього клієнта?"
      />
    </Box>
  );
};

export default Customers;
